import { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  List,
  MapPin,
  CaretLeft,
  CaretRight,
  MagnifyingGlass,
  Funnel,
  NavigationArrow,
  Clock,
  Speedometer,
  GasPump,
  Warning,
  Video,
  Path,
  Gear,
  SignOut,
  Bell,
  CaretDown,
  X,
  Car,
  Truck,
  Motorcycle,
  Check,
  Circle,
  Play,
  Pause,
  Users,
  CloudArrowUp,
  Eye,
  EyeSlash,
  Plus,
} from '@phosphor-icons/react';
import { devicesAPI } from '@/services/api';
import { useDevicesStore, useUIStore, useAuthStore, useBrandStore } from '@/store';
import { useNavigate } from 'react-router-dom';

// Fix for default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icons with directional arrow
const createCustomIcon = (status, bearing = 0) => {
  const colors = {
    moving: { bg: '#22c55e', border: '#16a34a' },      // Verde brillante
    online: { bg: '#3b82f6', border: '#2563eb' },      // Azul
    idle: { bg: '#f59e0b', border: '#d97706' },        // Amarillo/Naranja
    offline: { bg: '#ef4444', border: '#dc2626' },     // Rojo
    disconnected: { bg: '#9ca3af', border: '#6b7280' }, // Gris (7+ días sin conexión)
  };
  
  const { bg, border } = colors[status] || colors.offline;
  const rotation = bearing || 0;
  
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        width: 40px;
        height: 40px;
        position: relative;
        transform: rotate(${rotation}deg);
      ">
        <svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
          <!-- Flecha direccional -->
          <path d="M20 2 L32 35 L20 28 L8 35 Z" 
                fill="${bg}" 
                stroke="${border}" 
                stroke-width="2"
                style="filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));"
          />
          <!-- Círculo central blanco -->
          <circle cx="20" cy="20" r="6" fill="white" stroke="${border}" stroke-width="1"/>
        </svg>
      </div>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    popupAnchor: [0, -20],
  });
};

// Map view controller
const MapController = ({ center, zoom }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || 15, { animate: true });
    }
  }, [center, zoom, map]);
  
  return null;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuthStore();
  const { devices, setDevices, selectedDevice, setSelectedDevice, loading, setLoading } = useDevicesStore();
  const { sidebarCollapsed, toggleSidebar } = useUIStore();
  const { logoUrl, companyName } = useBrandStore();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [mapCenter, setMapCenter] = useState(null);
  const [mapZoom, setMapZoom] = useState(5);
  const [showDevicePanel, setShowDevicePanel] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [mapType, setMapType] = useState('hybrid'); // 'osm', 'satellite', 'hybrid'
  
  // Map tile configurations
  const mapTiles = {
    osm: {
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    },
    satellite: {
      url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      attribution: '&copy; Esri'
    },
    hybrid: {
      url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      attribution: '&copy; Esri'
    }
  };
  
  // Sorting and visibility
  const [sortBy, setSortBy] = useState('name'); // name, status, date
  const [visibleDevices, setVisibleDevices] = useState(new Set()); // IMEIs visibles en mapa
  const [allVisible, setAllVisible] = useState(true);
  
  // Groups
  const [groups, setGroups] = useState(() => {
    const saved = localStorage.getItem('4sat_device_groups');
    return saved ? JSON.parse(saved) : [{ id: 'default', name: 'Mis Vehículos', expanded: true, devices: [] }];
  });
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');

  // Initialize visible devices when devices load
  useEffect(() => {
    if (devices.length > 0) {
      setVisibleDevices(new Set(devices.map(d => d.imei)));
      setAllVisible(true);
    }
  }, [devices]);

  // Save groups to localStorage
  useEffect(() => {
    localStorage.setItem('4sat_device_groups', JSON.stringify(groups));
  }, [groups]);

  // Toggle device visibility on map
  const toggleDeviceVisibility = (imei) => {
    setVisibleDevices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(imei)) {
        newSet.delete(imei);
      } else {
        newSet.add(imei);
      }
      return newSet;
    });
  };

  // Toggle all devices visibility
  const toggleAllVisibility = () => {
    if (allVisible) {
      setVisibleDevices(new Set());
    } else {
      setVisibleDevices(new Set(devices.map(d => d.imei)));
    }
    setAllVisible(!allVisible);
  };

  // Add new group
  const addGroup = () => {
    if (!newGroupName.trim()) return;
    setGroups(prev => [...prev, { 
      id: `group_${Date.now()}`, 
      name: newGroupName.trim(), 
      expanded: true, 
      devices: [] 
    }]);
    setNewGroupName('');
    setShowGroupModal(false);
  };

  // Toggle group expanded
  const toggleGroupExpanded = (groupId) => {
    setGroups(prev => prev.map(g => 
      g.id === groupId ? { ...g, expanded: !g.expanded } : g
    ));
  };

  // Move device to group
  const moveDeviceToGroup = (imei, groupId) => {
    setGroups(prev => prev.map(g => ({
      ...g,
      devices: g.id === groupId 
        ? [...new Set([...g.devices, imei])]
        : g.devices.filter(d => d !== imei)
    })));
  };

  // Fetch devices from all Tracksolid accounts
  const fetchDevices = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('4sat_token');
      
      // Use aggregated endpoint that includes all Tracksolid accounts
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/tracksolid/devices/aggregated`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const allDevices = response.data?.devices || [];
      
      // Process devices with status calculation
      const processedDevices = allDevices.map(device => {
        const speed = parseFloat(device.speed) || 0;
        const accOn = device.accStatus === 1 || device.accStatus === '1';
        const isOnline = device.status === 1 || device.status === '1';
        
        // Check if disconnected for more than 7 days
        const lastUpdate = device.gpsTime || device.positionTime;
        const daysSinceUpdate = lastUpdate ? 
          (Date.now() - new Date(lastUpdate).getTime()) / (1000 * 60 * 60 * 24) : 999;
        
        let deviceStatus = 'offline';
        if (daysSinceUpdate > 7) {
          deviceStatus = 'disconnected';
        } else if (speed > 0) {
          deviceStatus = 'moving';
        } else if (accOn && isOnline) {
          deviceStatus = 'idle';
        } else if (isOnline) {
          deviceStatus = 'online';
        }
        
        return {
          ...device,
          status: deviceStatus,
          bearing: device.direction || 0,
          visible: true,
          accountName: device._account_name || device._account,
        };
      });
      
      setDevices(processedDevices);
      
      // Set initial map center
      if (processedDevices.length > 0 && processedDevices[0].lat) {
        setMapCenter([parseFloat(processedDevices[0].lat), parseFloat(processedDevices[0].lng)]);
      }
    } catch (err) {
      console.error('Error fetching devices:', err);
      // Fallback to original method if aggregated fails
      try {
        const response = await devicesAPI.getAll();
        setDevices(response.data || []);
      } catch (fallbackErr) {
        console.error('Fallback also failed:', fallbackErr);
      }
    } finally {
      setLoading(false);
    }
  }, [setDevices, setLoading]);

  useEffect(() => {
    fetchDevices();
    
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      setRefreshing(true);
      fetchDevices().finally(() => setRefreshing(false));
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchDevices]);

  // Filter devices
  const filteredDevices = devices.filter(device => {
    const matchesSearch = device.deviceName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          device.imei?.includes(searchTerm);
    const matchesStatus = statusFilter === 'all' || device.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Select device and center map
  const handleSelectDevice = (device) => {
    setSelectedDevice(device);
    setShowDevicePanel(true);
    if (device.lat && device.lng) {
      setMapCenter([parseFloat(device.lat), parseFloat(device.lng)]);
      setMapZoom(16);
    }
  };

  // Get status counts
  const statusCounts = {
    all: devices.length,
    online: devices.filter(d => d.status === 'online').length,
    moving: devices.filter(d => d.status === 'moving').length,
    idle: devices.filter(d => d.status === 'idle').length,
    offline: devices.filter(d => d.status === 'offline').length,
    disconnected: devices.filter(d => d.status === 'disconnected').length,
  };

  // Sort devices
  const sortedDevices = [...filteredDevices].sort((a, b) => {
    switch (sortBy) {
      case 'status':
        const statusOrder = { moving: 0, idle: 1, online: 2, offline: 3, disconnected: 4 };
        return (statusOrder[a.status] || 5) - (statusOrder[b.status] || 5);
      case 'date':
        const dateA = new Date(a.gpsTime || a.positionTime || 0).getTime();
        const dateB = new Date(b.gpsTime || b.positionTime || 0).getTime();
        return dateB - dateA;
      case 'name':
      default:
        return (a.deviceName || a.imei).localeCompare(b.deviceName || b.imei);
    }
  });

  // Logout handler
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Get device icon based on type
  const getDeviceIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'truck':
        return <Truck size={16} />;
      case 'motorcycle':
        return <Motorcycle size={16} />;
      default:
        return <Car size={16} />;
    }
  };

  // Format speed
  const formatSpeed = (speed) => {
    return speed ? `${Math.round(speed)} km/h` : '0 km/h';
  };

  // Format time - formato: dd-mm-yy, hh:mm
  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year}, ${hours}:${minutes}`;
  };

  return (
    <div className="h-screen w-screen flex flex-col overflow-hidden bg-gray-100" data-testid="dashboard">
      {/* Top Navigation Bar */}
      <header className="h-14 bg-white border-b border-gray-200 flex items-center px-2 sm:px-4 shrink-0 z-40 shadow-sm">
        {/* Logo */}
        <div className="flex items-center gap-2 sm:gap-3 mr-2 sm:mr-8">
          {logoUrl ? (
            <img src={logoUrl} alt={companyName} className="h-10 sm:h-12 object-contain max-w-[140px]" />
          ) : (
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
                <MapPin size={20} weight="fill" className="text-white sm:hidden" />
                <MapPin size={24} weight="fill" className="text-white hidden sm:block" />
              </div>
              <span className="font-bold text-gray-800 text-lg hidden sm:block">{companyName}</span>
            </div>
          )}
        </div>
        
        {/* Navigation Links - Hidden on mobile */}
        <nav className="hidden md:flex items-center gap-1 flex-1">
          <button 
            className="px-4 py-2 rounded-lg text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200"
            data-testid="nav-monitor"
          >
            <span className="flex items-center gap-2">
              <MapPin size={18} weight="duotone" />
              <span className="hidden md:inline">Monitor</span>
            </span>
          </button>
          <button 
            onClick={() => navigate('/tracks')}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            data-testid="nav-tracks"
          >
            <span className="flex items-center gap-2">
              <Path size={18} />
              <span className="hidden md:inline">Recorridos</span>
            </span>
          </button>
          <button 
            onClick={() => navigate('/reports')}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            data-testid="nav-reports"
          >
            <span className="flex items-center gap-2">
              <List size={18} />
              <span className="hidden md:inline">Reportes</span>
            </span>
          </button>
          <button 
            onClick={() => navigate('/video')}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            data-testid="nav-video"
          >
            <span className="flex items-center gap-2">
              <Video size={18} />
              <span className="hidden md:inline">Video</span>
            </span>
          </button>
          <button 
            onClick={() => navigate('/alerts')}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            data-testid="nav-alerts"
          >
            <span className="flex items-center gap-2">
              <Bell size={18} />
              <span className="hidden md:inline">Alertas</span>
            </span>
          </button>
          <button 
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            data-testid="nav-geofences"
          >
            <span className="flex items-center gap-2">
              <Funnel size={18} />
              <span className="hidden md:inline">Geocercas</span>
            </span>
          </button>
        </nav>
        
        {/* Mobile Navigation Icons */}
        <div className="flex md:hidden items-center gap-1 flex-1 justify-center overflow-x-auto">
          <button 
            onClick={() => navigate('/tracks')}
            className="p-2 rounded-lg text-gray-600 hover:bg-gray-100"
            title="Recorridos"
          >
            <Path size={20} />
          </button>
          <button 
            onClick={() => navigate('/video')}
            className="p-2 rounded-lg text-gray-600 hover:bg-gray-100"
            title="Video"
          >
            <Video size={20} />
          </button>
          <button 
            onClick={() => navigate('/alerts')}
            className="p-2 rounded-lg text-gray-600 hover:bg-gray-100"
            title="Alertas"
          >
            <Bell size={20} />
          </button>
        </div>
        
        {/* Right side actions */}
        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          <button 
            onClick={() => navigate('/bridge')}
            className="p-2 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors hidden sm:flex"
            title="Configuración del Puente 4Sat"
          >
            <CloudArrowUp size={20} />
          </button>
          <button 
            onClick={() => navigate('/settings')}
            className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            title="Configuración"
          >
            <Gear size={20} />
          </button>
          <div className="flex items-center gap-1 sm:gap-2 ml-1 sm:ml-2 pl-1 sm:pl-2 border-l border-gray-200">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium text-xs sm:text-sm">
              {user?.account?.charAt(0).toUpperCase() || 'U'}
            </div>
            <span className="text-sm text-gray-600 hidden lg:block max-w-[120px] truncate">
              {user?.account || 'Usuario'}
            </span>
            <button 
              onClick={handleLogout}
              className="p-2 rounded-lg text-red-500 hover:text-red-600 hover:bg-red-50 transition-colors"
              title="Cerrar sesión"
              data-testid="logout-button"
            >
              <SignOut size={20} />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <aside 
          className={`${sidebarCollapsed ? 'w-16' : 'w-72'} 
            flex flex-col bg-white border-r border-gray-200 z-30 shrink-0 shadow-sm`}
          style={{ transition: 'width 0.3s ease-in-out' }}
          data-testid="sidebar"
        >
          {/* Search & Filter */}
          {!sidebarCollapsed && (
            <div className="p-3 space-y-3 border-b border-gray-200">
            <div className="relative">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar dispositivo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="search-devices-input"
                className="w-full h-9 pl-9 pr-3 rounded-lg bg-gray-50 border border-gray-200 text-gray-800 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all outline-none"
              />
            </div>
            
            {/* Status filters - colorful badges */}
            <div className="flex items-center gap-2 text-xs flex-wrap">
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-2 py-1 rounded font-bold ${statusFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
              >
                {statusCounts.all}
              </button>
              <button
                onClick={() => setStatusFilter('moving')}
                className={`flex items-center gap-1 ${statusFilter === 'moving' ? 'opacity-100' : 'opacity-70 hover:opacity-100'}`}
              >
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                <span className="text-green-600 font-medium">{statusCounts.moving}</span>
              </button>
              <button
                onClick={() => setStatusFilter('idle')}
                className={`flex items-center gap-1 ${statusFilter === 'idle' ? 'opacity-100' : 'opacity-70 hover:opacity-100'}`}
              >
                <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                <span className="text-yellow-600 font-medium">{statusCounts.idle}</span>
              </button>
              <button
                onClick={() => setStatusFilter('online')}
                className={`flex items-center gap-1 ${statusFilter === 'online' ? 'opacity-100' : 'opacity-70 hover:opacity-100'}`}
              >
                <span className="w-2 h-2 rounded-full bg-blue-400"></span>
                <span className="text-blue-600 font-medium">{statusCounts.online}</span>
              </button>
              <button
                onClick={() => setStatusFilter('offline')}
                className={`flex items-center gap-1 ${statusFilter === 'offline' ? 'opacity-100' : 'opacity-70 hover:opacity-100'}`}
              >
                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                <span className="text-red-600 font-medium">{statusCounts.offline}</span>
              </button>
              <button
                onClick={() => setStatusFilter('disconnected')}
                className={`flex items-center gap-1 ${statusFilter === 'disconnected' ? 'opacity-100' : 'opacity-70 hover:opacity-100'}`}
              >
                <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                <span className="text-gray-500 font-medium">{statusCounts.disconnected}</span>
              </button>
            </div>
            
            {/* Sort and visibility controls */}
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Ordenar:</span>
                <select 
                  value={sortBy} 
                  onChange={(e) => setSortBy(e.target.value)}
                  className="bg-gray-50 border border-gray-200 rounded px-2 py-1 text-gray-700 focus:outline-none focus:border-blue-500"
                >
                  <option value="name">Nombre</option>
                  <option value="status">Estado</option>
                  <option value="date">Fecha</option>
                </select>
              </div>
              <button
                onClick={toggleAllVisibility}
                className="flex items-center gap-1 text-gray-500 hover:text-blue-600"
                title={allVisible ? 'Ocultar todos' : 'Mostrar todos'}
              >
                {allVisible ? <Eye size={14} /> : <EyeSlash size={14} />}
                <span>{allVisible ? 'Ocultar' : 'Mostrar'}</span>
              </button>
            </div>
          </div>
        )}

        {/* Device List - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          {loading && !devices.length ? (
            <div className="flex items-center justify-center h-32 text-gray-400">
              <div className="spinner">
                <Circle size={24} weight="bold" />
              </div>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {/* Groups */}
              {groups.map(group => {
                const groupDevices = sortedDevices.filter(d => 
                  group.id === 'default' 
                    ? !groups.some(g => g.id !== 'default' && g.devices.includes(d.imei))
                    : group.devices.includes(d.imei)
                );
                
                if (groupDevices.length === 0 && group.id !== 'default') return null;
                
                return (
                  <div key={group.id}>
                    {/* Group header */}
                    <div 
                      className="px-3 py-2 flex items-center justify-between text-sm bg-gray-50 cursor-pointer hover:bg-gray-100"
                      onClick={() => toggleGroupExpanded(group.id)}
                    >
                      <div className="flex items-center gap-2 text-gray-700">
                        {group.expanded ? <CaretDown size={14} /> : <CaretRight size={14} />}
                        <span className="font-medium">{group.name}</span>
                      </div>
                      <span className="text-gray-500">{groupDevices.length}</span>
                    </div>
                    
                    {/* Group devices */}
                    {group.expanded && groupDevices.map((device) => (
                      <div
                        key={device.imei}
                        className={`p-3 cursor-pointer transition-colors hover:bg-gray-50 ${
                          selectedDevice?.imei === device.imei ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                        }`}
                      >
                        {sidebarCollapsed ? (
                          <div className="flex justify-center">
                            <div className={`w-3 h-3 rounded-full ${
                              device.status === 'moving' ? 'bg-green-500' :
                              device.status === 'idle' ? 'bg-yellow-500' : 
                              device.status === 'online' ? 'bg-blue-500' : 
                              device.status === 'disconnected' ? 'bg-gray-400' : 'bg-red-500'
                            }`} />
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            {/* Checkbox for visibility */}
                            <input
                              type="checkbox"
                              checked={visibleDevices.has(device.imei)}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleDeviceVisibility(device.imei);
                              }}
                              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            
                            {/* Status indicator */}
                            <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                              device.status === 'moving' ? 'bg-green-500' :
                              device.status === 'idle' ? 'bg-yellow-500' : 
                              device.status === 'online' ? 'bg-blue-500' : 
                              device.status === 'disconnected' ? 'bg-gray-400' : 'bg-red-500'
                            }`} />
                            
                            {/* Device info */}
                            <div 
                              className="flex-1 min-w-0"
                              onClick={() => handleSelectDevice(device)}
                            >
                              <div className="font-medium text-sm truncate text-gray-800">
                                {device.deviceName || device.imei}
                              </div>
                              <div className="text-xs text-gray-500">
                                {formatTime(device.gpsTime || device.positionTime)}, {formatSpeed(device.speed)}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
              
              {/* Add group button */}
              {!sidebarCollapsed && (
                <div className="p-2 border-t border-gray-200">
                  <button
                    onClick={() => setShowGroupModal(true)}
                    className="w-full py-2 text-xs text-blue-600 hover:bg-blue-50 rounded flex items-center justify-center gap-1"
                  >
                    <Plus size={14} />
                    Crear Grupo
                  </button>
                </div>
              )}
              
              {filteredDevices.length === 0 && (
                <div className="p-6 text-center text-gray-500 text-sm">
                  No se encontraron dispositivos
                </div>
              )}
            </div>
          )}
        </div>

        {/* Collapse button at bottom */}
        <div className="border-t border-gray-200 p-2 shrink-0 bg-white">
          <button
            onClick={toggleSidebar}
            className="w-full flex items-center justify-center gap-2 p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 text-sm"
            data-testid="toggle-sidebar-btn"
          >
            {sidebarCollapsed ? (
              <CaretRight size={16} />
            ) : (
              <>
                <CaretLeft size={16} />
                <span>Colapsar</span>
              </>
            )}
          </button>
        </div>
      </aside>

      {/* Main Content - Map */}
      <main className="flex-1 relative">
        {/* Map */}
        <MapContainer
          center={mapCenter || [20, 0]}
          zoom={mapZoom}
          className="h-full w-full z-0"
          zoomControl={false}
          data-testid="map-container"
        >
          <TileLayer
            attribution={mapTiles[mapType].attribution}
            url={mapTiles[mapType].url}
          />
          {mapType === 'hybrid' && (
            <TileLayer
              url="https://stamen-tiles-{s}.a.ssl.fastly.net/toner-labels/{z}/{x}/{y}{r}.png"
              opacity={0.6}
            />
          )}
          
          {/* Map type selector */}
          <div className="absolute top-3 right-3 z-[1000] bg-white rounded-lg shadow-lg p-1 flex gap-1">
            <button
              onClick={() => setMapType('osm')}
              className={`px-2 py-1 text-xs rounded transition-colors ${mapType === 'osm' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              Mapa
            </button>
            <button
              onClick={() => setMapType('satellite')}
              className={`px-2 py-1 text-xs rounded transition-colors ${mapType === 'satellite' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              Satélite
            </button>
            <button
              onClick={() => setMapType('hybrid')}
              className={`px-2 py-1 text-xs rounded transition-colors ${mapType === 'hybrid' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              Híbrido
            </button>
          </div>
          
          {mapCenter && <MapController center={mapCenter} zoom={mapZoom} />}
          
          {/* Device Markers - Only show visible devices */}
          {filteredDevices
            .filter(device => visibleDevices.has(device.imei) && device.lat && device.lng)
            .map((device) => (
              <Marker
                key={`marker-${device.imei}`}
                position={[parseFloat(device.lat), parseFloat(device.lng)]}
                icon={createCustomIcon(device.status, parseFloat(device.direction) || parseFloat(device.bearing) || 0)}
                eventHandlers={{
                  click: () => handleSelectDevice(device),
                }}
              >
                <Popup>
                  <div className="min-w-[200px] p-1">
                    <h3 className="font-bold text-sm mb-2">{device.deviceName || device.imei}</h3>
                    <div className="text-xs space-y-1 text-gray-600">
                      <p><strong>Velocidad:</strong> {formatSpeed(device.speed)}</p>
                      <p><strong>Dirección:</strong> {device.address || 'N/A'}</p>
                      <p><strong>Última actualización:</strong> {formatTime(device.gpsTime)}</p>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
        </MapContainer>

        {/* Top Bar */}
        <div className="absolute top-4 right-4 z-10 flex gap-2">
          <div className="glass rounded-lg px-3 py-2 flex items-center gap-2 text-sm">
            <div className={`w-2 h-2 rounded-full ${refreshing ? 'bg-yellow-500 animate-pulse' : 'bg-secondary'}`} />
            <span>{devices.length} dispositivos</span>
          </div>
        </div>

        {/* Device Detail Panel */}
        {showDevicePanel && selectedDevice && (
          <div 
            className="floating-panel glass top-4 left-4 w-80 max-h-[calc(100vh-120px)] overflow-y-auto fade-in"
            data-testid="device-detail-panel"
          >
            <div className="p-4">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  {getDeviceIcon(selectedDevice.deviceType)}
                  <h3 className="font-bold">{selectedDevice.deviceName || selectedDevice.imei}</h3>
                </div>
                <button
                  onClick={() => setShowDevicePanel(false)}
                  className="p-1 rounded hover:bg-muted transition-colors"
                  data-testid="close-device-panel-btn"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Status */}
              <div className="flex items-center gap-2 mb-4">
                <span className={`status-badge ${
                  selectedDevice.status === 'online' ? 'status-online' :
                  selectedDevice.status === 'moving' ? 'status-moving' :
                  selectedDevice.status === 'idle' ? 'status-idle' : 'status-offline'
                }`}>
                  {selectedDevice.status || 'offline'}
                </span>
                <span className="text-xs text-muted-foreground">
                  IMEI: {selectedDevice.imei}
                </span>
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-muted/30 rounded-lg p-3">
                  <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                    <Speedometer size={12} /> Velocidad
                  </div>
                  <div className="font-bold">{formatSpeed(selectedDevice.speed)}</div>
                </div>
                <div className="bg-muted/30 rounded-lg p-3">
                  <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                    <NavigationArrow size={12} /> Dirección
                  </div>
                  <div className="font-bold">{selectedDevice.course || 0}°</div>
                </div>
                <div className="bg-muted/30 rounded-lg p-3">
                  <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                    <GasPump size={12} /> Combustible
                  </div>
                  <div className="font-bold">{selectedDevice.fuel || '-'}%</div>
                </div>
                <div className="bg-muted/30 rounded-lg p-3">
                  <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                    <Clock size={12} /> Última señal
                  </div>
                  <div className="font-bold text-xs">{formatTime(selectedDevice.gpsTime)}</div>
                </div>
              </div>

              {/* Address */}
              <div className="bg-muted/30 rounded-lg p-3 mb-4">
                <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                  <MapPin size={12} /> Ubicación
                </div>
                <div className="text-sm">{selectedDevice.address || 'Dirección no disponible'}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {selectedDevice.lat}, {selectedDevice.lng}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => navigate(`/tracks/${selectedDevice.imei}`)}
                  className="flex-1 h-9 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-1"
                  data-testid="view-track-btn"
                >
                  <Path size={16} /> Recorrido
                </button>
                <button
                  onClick={() => navigate(`/video/${selectedDevice.imei}`)}
                  className="flex-1 h-9 rounded-lg bg-secondary text-secondary-foreground text-sm font-medium hover:bg-secondary/90 transition-colors flex items-center justify-center gap-1"
                  data-testid="view-video-btn"
                >
                  <Video size={16} /> Video
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Zoom Controls */}
        <div className="absolute bottom-6 right-4 z-10 flex flex-col gap-1">
          <button 
            onClick={() => setMapZoom(z => Math.min(z + 1, 18))}
            className="glass w-10 h-10 rounded-lg flex items-center justify-center hover:bg-muted/50 transition-colors text-lg font-bold"
          >
            +
          </button>
          <button 
            onClick={() => setMapZoom(z => Math.max(z - 1, 2))}
            className="glass w-10 h-10 rounded-lg flex items-center justify-center hover:bg-muted/50 transition-colors text-lg font-bold"
          >
            -
          </button>
        </div>
      </main>
      </div>

      {/* Modal for creating groups */}
      {showGroupModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Crear Nuevo Grupo</h3>
            <input
              type="text"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="Nombre del grupo..."
              className="w-full h-10 px-3 rounded-lg border border-gray-200 text-gray-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none mb-4"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowGroupModal(false)}
                className="px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                onClick={addGroup}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              >
                Crear
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
